import React, { useEffect, useState } from "react";
import {
  ChartBarIcon,
  CurrencyDollarIcon,
  ClockIcon,
  ArrowTrendingUpIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";
import {
  getDealerFinanceDashboard,
  getDealerBalances,
} from "../../api/finansAgro";

const formatAmount = (n) =>
  new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 0 }).format(n || 0);

const StatCard = ({ icon: Icon, label, value, suffix = "₸", accent = "blue" }) => {
  const accentMap = {
    blue: "from-blue-500/20 to-blue-500/0 border-blue-500/30 text-blue-400",
    green: "from-green-500/20 to-green-500/0 border-green-500/30 text-green-400",
    amber: "from-amber-500/20 to-amber-500/0 border-amber-500/30 text-amber-400",
    purple: "from-purple-500/20 to-purple-500/0 border-purple-500/30 text-purple-400",
  };
  return (
    <div className={`dark-card rounded-xl p-5 border bg-gradient-to-br ${accentMap[accent]}`}>
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center">
          <Icon className="w-5 h-5" />
        </div>
        <div className="text-sm text-text-secondary">{label}</div>
      </div>
      <div className="text-2xl font-bold text-white">
        {formatAmount(value)} <span className="text-base text-text-secondary">{suffix}</span>
      </div>
    </div>
  );
};

const FinanceDashboard = () => {
  const [stats, setStats] = useState(null);
  const [balances, setBalances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const [dash, bals] = await Promise.all([
        getDealerFinanceDashboard().catch((e) => ({ __error: e.message })),
        getDealerBalances().catch((e) => ({ items: [], __error: e.message })),
      ]);
      if (dash && dash.__error) {
        setError(dash.__error);
        setStats(null);
      } else {
        setStats(dash);
      }
      setBalances(bals.items || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-accent-grad rounded-xl flex items-center justify-center">
            <ChartBarIcon className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">Finance Dashboard</h2>
            <p className="text-sm text-text-secondary">Dealer финанс жалпы көрсеткіштері</p>
          </div>
        </div>
        <button
          onClick={fetchAll}
          disabled={loading}
          className="p-2 rounded-lg border border-gray-700 text-text-secondary hover:bg-bg-primary"
          title="Refresh"
        >
          <ArrowPathIcon className={`w-5 h-5 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {error && (
        <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={CurrencyDollarIcon}
          label="Total commissions"
          value={stats?.total_commissions}
          accent="green"
        />
        <StatCard
          icon={ArrowTrendingUpIcon}
          label="Dealer credits"
          value={stats?.total_dealer_credits}
          accent="blue"
        />
        <StatCard
          icon={ClockIcon}
          label="Pending withdrawals"
          value={stats?.pending_withdrawals_amount}
          accent="amber"
        />
        <StatCard
          icon={CurrencyDollarIcon}
          label="Completed withdrawals"
          value={stats?.completed_withdrawals_amount}
          accent="purple"
        />
      </div>

      <div className="dark-card rounded-xl overflow-hidden">
        <div className="p-4 border-b border-gray-800 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white">Dealer balances</h3>
          <span className="text-xs text-text-secondary">{balances.length} dealer</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-bg-primary text-text-secondary text-sm">
              <tr>
                <th className="px-4 py-3 text-left">ID</th>
                <th className="px-4 py-3 text-left">Атау</th>
                <th className="px-4 py-3 text-left">Phone</th>
                <th className="px-4 py-3 text-left">Role</th>
                <th className="px-4 py-3 text-right">Баланс</th>
                <th className="px-4 py-3 text-right">Pending</th>
              </tr>
            </thead>
            <tbody>
              {loading && balances.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-text-secondary">
                    Жүктелуде...
                  </td>
                </tr>
              )}
              {!loading && balances.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-text-secondary">
                    Dealer жоқ
                  </td>
                </tr>
              )}
              {balances.map((d) => (
                <tr key={d.user_id} className="border-t border-gray-800">
                  <td className="px-4 py-3 text-white font-mono">{d.user_id}</td>
                  <td className="px-4 py-3 text-white">{d.name || "—"}</td>
                  <td className="px-4 py-3 text-text-secondary font-mono text-sm">
                    {d.phone_number || "—"}
                  </td>
                  <td className="px-4 py-3 text-text-secondary text-sm">
                    {d.dealer_role || "—"}
                  </td>
                  <td className="px-4 py-3 text-right text-white font-semibold">
                    {formatAmount(d.balance)} ₸
                  </td>
                  <td className="px-4 py-3 text-right text-amber-400">
                    {d.pending_withdrawal > 0 ? `${formatAmount(d.pending_withdrawal)} ₸` : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default FinanceDashboard;
